(() => {
  const priceSelector = [
    '.kaching-bundles__bar-price',
    '.kaching-bundles__bar-full-price',
    '.kaching-bundles__bundle-products__price',
    '.kaching-bundles__bundle-products__full-price',
    '.kaching-bundles-atc-total'
  ].join(',');

  function currencyDigits(currency) {
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits;
    } catch {
      return 2;
    }
  }

  function decimalSymbol(locale) {
    try {
      return new Intl.NumberFormat(locale).formatToParts(1.1).find((part) => part.type === 'decimal')?.value || '.';
    } catch {
      return '.';
    }
  }

  function parseMoney(text, currency, locale) {
    const matches = String(text).replace(/\u2212/g, '-').match(/-?\d[\d.,'’\s\u00a0\u202f]*/g);
    if (!matches || matches.length !== 1) return null;
    if (/\d[.,]\d+\s+\d[.,]\d/.test(matches[0])) return null;
    let value = matches[0].trim().replace(/[\s'’\u00a0\u202f]/g, '');
    if (!value.startsWith('-') && /[-\u2212][^\d]*\d/.test(String(text))) value = `-${value}`;
    const digits = currencyDigits(currency);
    const dot = value.lastIndexOf('.');
    const comma = value.lastIndexOf(',');
    let decimal = '';
    if (dot !== -1 && comma !== -1) {
      decimal = dot > comma ? '.' : ',';
    } else if (dot !== -1 || comma !== -1) {
      const separator = dot !== -1 ? '.' : ',';
      const groups = value.split(separator);
      const tail = groups[groups.length - 1].length;
      if (tail > 0 && tail < 3) decimal = separator;
      if (tail === 3 && digits === 3 && separator === decimalSymbol(locale) && groups.length === 2) decimal = separator;
    }
    let normalized = value;
    if (decimal) {
      const split = value.lastIndexOf(decimal);
      const integer = value.slice(0, split).replace(/[.,]/g, '');
      const fraction = value.slice(split + 1);
      const integerGroups = value.slice(0, split).split(/[.,]/);
      if (integerGroups.slice(1).some((group) => !/^\d{2,3}$/.test(group))) return null;
      if (!/^\d+$/.test(fraction)) return null;
      if (fraction.length > digits && Number(fraction.slice(digits)) !== 0) return null;
      normalized = `${integer}.${fraction}`;
    } else {
      if (value.split(/[.,]/).slice(1).some((group) => !/^\d{2,3}$/.test(group))) return null;
      normalized = value.replace(/[.,]/g, '');
    }
    if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null;
    const number = Number(normalized);
    const minor = Math.round(number * 10 ** digits);
    return Number.isSafeInteger(minor) ? minor : null;
  }

  function formatMoney(minor, currency, locale) {
    const value = minor / 10 ** currencyDigits(currency);
    let formatted;
    try {
      formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: currencyDigits(currency)
      }).format(value);
    } catch {
      formatted = new Intl.NumberFormat('en', {
        minimumFractionDigits: 0,
        maximumFractionDigits: currencyDigits(currency)
      }).format(value);
    }
    return new RegExp(`\\b${currency}\\b`).test(formatted) ? formatted : `${formatted} ${currency}`;
  }

  function fillAmount(template, amount) {
    return String(template).replace(/\[amount\]|\{\{\s*amount\s*\}\}/g, amount);
  }

  function savingsAmount(original, current) {
    return Number.isSafeInteger(original) && Number.isSafeInteger(current) && original > current && current >= 0
      ? original - current
      : null;
  }

  function savingLabel(text, template, amount) {
    const match = String(text).match(/\b(?:save|ahorras?|ahorro)\b/i);
    return match ? `${text.slice(0, match.index)}${fillAmount(template, amount)}` : null;
  }

  function priceText(element) {
    return Array.from(element.childNodes)
      .filter((node) => node.nodeType === 3 || (node.nodeType === 1 && !node.hasAttribute('data-vasenia-currency-code')))
      .map((node) => node.textContent)
      .join('');
  }

  function setText(element, text) {
    const nodes = Array.from(element.childNodes).filter((node) => node.nodeType === 3);
    const primary = nodes.find((node) => node.textContent.trim());
    if (!primary || nodes.filter((node) => node.textContent.trim()).length !== 1) return;
    if (primary.textContent !== text) primary.textContent = text;
  }

  function addCurrency(element, currency, locale) {
    const existing = element.querySelector('[data-vasenia-currency-code]');
    const original = priceText(element);
    const alreadyLabeled = new RegExp(`\\b${currency}\\b`, 'i').test(original);
    if (alreadyLabeled || parseMoney(original, currency, locale) === null) {
      existing?.remove();
      return;
    }
    if (existing) {
      if (existing.textContent !== ` ${currency}`) existing.textContent = ` ${currency}`;
      return;
    }
    const code = element.ownerDocument.createElement('span');
    code.className = 'vasenia-currency-code';
    code.setAttribute('data-vasenia-currency-code', '');
    code.textContent = ` ${currency}`;
    element.append(code);
  }

  function enhance(product) {
    const currency = product.dataset.currency?.toUpperCase();
    if (!/^[A-Z]{3}$/.test(currency || '')) return;
    const locale = product.dataset.locale || document.documentElement.lang || 'en';
    const saveTemplate = product.dataset.saveTemplate || 'Save [amount]';
    const addTemplate = product.dataset.addTemplate || 'Add the new Romper for [amount]';
    const isLibrary = /\/products\/complete-vasenia-pattern-library(?:$|[?#])/.test(product.dataset.url || '');
    const scope = product.querySelector('.product__info-container') || product;
    let scheduled = false;

    const refresh = () => {
      scheduled = false;
      observer.disconnect();
      try {
        scope.querySelectorAll(priceSelector).forEach((price) => addCurrency(price, currency, locale));
        const priceOf = (bar, selector) => {
          const price = bar.querySelector(selector);
          return price ? parseMoney(priceText(price), currency, locale) : null;
        };
        scope.querySelectorAll('.kaching-bundles__bars').forEach((group) => {
          const bars = Array.from(group.querySelectorAll('.kaching-bundles__bar'));
          bars.forEach((bar, index) => {
          const current = priceOf(bar, '.kaching-bundles__bar-price');
          const original = priceOf(bar, '.kaching-bundles__bar-full-price');
          const savings = savingsAmount(original, current);
          if (savings !== null) {
            const amount = formatMoney(savings, currency, locale);
            bar.querySelectorAll('.kaching-bundles__bar-subtitle, .kaching-bundles__bar-label').forEach((label) => {
              const replacement = savingLabel(label.textContent, saveTemplate, amount);
              if (replacement) setText(label, replacement);
            });
          }
          if (isLibrary && index > 0) {
            const baseline = priceOf(bars[0], '.kaching-bundles__bar-price');
            const increment = savingsAmount(current, baseline);
            const title = bar.querySelector('.kaching-bundles__bar-title')?.textContent || '';
            const subtitle = bar.querySelector('.kaching-bundles__bar-subtitle');
            if (increment !== null && /romper/i.test(title) && subtitle) {
              setText(subtitle, fillAmount(addTemplate, formatMoney(increment, currency, locale)));
            }
          }
          });
        });
      } finally {
        if (product.isConnected) observer.observe(scope, { childList: true, subtree: true, characterData: true });
      }
    };

    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(refresh);
    });
    refresh();
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { currencyDigits, parseMoney, formatMoney, fillAmount, savingsAmount, savingLabel, priceText, setText, addCurrency };
  }
  if (typeof document === 'undefined') return;
  const initialized = new WeakSet();
  const initialize = () => {
    document.querySelectorAll('.vasenia-pdf-product[data-currency]').forEach((product) => {
      if (initialized.has(product)) return;
      initialized.add(product);
      enhance(product);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
